import { Component, inject, signal } from '@angular/core';
import { AuditService } from '../../../services/audit';
import { ApiEndpoints } from '../../../core/api-endpoints';
import { FlashService } from '../../../services/flash';
import { FormsModule } from '@angular/forms';
import { LoadingIndicator } from '../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../shared/pagination/pagination';
import { IAudits } from '../../../interfaces/audit';
import { ISchools } from '../../../interfaces/school';
import { Helpers } from '../../../utilities/helpers';
import { NgClass } from '@angular/common';
import { Session } from '../../../services/session';
import { SchoolService } from '../../../services/school';
import { DashboardService } from '../../../services/dashboard';
import { ShimmerEffect } from '../../../shared/shimmer-effect/shimmer-effect';

@Component({
  selector: 'app-audit-logs',
  imports: [FormsModule, LoadingIndicator, NotFoundError, Pagination, NgClass, ShimmerEffect],
  templateUrl: './audit-logs.html',
  styleUrl: './audit-logs.css',
})
export class AuditLogs {
  constructor(
    private auditService: AuditService,
    private session: Session,
    private schoolService: SchoolService,
    private dashboardService: DashboardService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }

    if (this.loggedUserRole !== 'SUPER_ADMIN') {
      this.getDashboardData();
    }

    this.getAuditLogs();
  }

  protected isFiltering = signal<boolean>(false);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected search: string = '';
  protected pagination = signal({ page: 0, size: 10 });
  protected filterSchoolId: string = '';
  protected schoolId?: string;
  protected readonly flashService = inject(FlashService);
  protected audits = signal<IAudits | null>(null);
  protected loggedUserRole?: string;
  protected schools = signal<ISchools | null>(null);
  protected type: string = '';
  protected feature: string = '';
  protected readonly Helpers = Helpers;
  protected dashboardData = signal<any>(null);
  protected isLoading = signal<boolean>(false);

  protected getAuditLogs(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.auditService.getAudits(`${ApiEndpoints.url}/audit-logs`, params).subscribe({
      next: (res) => {
        this.audits.set(res);
        this.isFiltering.set(false);
      },
      error: (err) => {
        this.isFiltering.set(false);
        this.flashService.failure(err?.error?.message ?? 'Could not load audit logs. Refresh the page');
      },
    });
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getAuditLogs();
  }

  protected getDashboardData(): void {
    this.isLoading.set(true);

    this.dashboardService
      .getDashboardData(`${ApiEndpoints.url}/dashboard/audit-stats`)
      .subscribe({
        next: (res) => {
          this.dashboardData.set(res.payload);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Failed to load dashboard. Refresh the page.');
        },
      });
  }
}
